import React, { useMemo } from 'react';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import DragIndicatorIcon from 'icons/drag-indicator.svg';
import LockIcon from 'icons/ic-lock-20.svg';
import InfoIcon from 'icons/info.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import CheckboxField from 'components/application/CheckboxField';
import SidePanel from 'components/application/SidePanel';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { buildColumnNames } from 'components/projectsDashboard/components/ProjectsTable';

export default function EditColumnsSidePanel({
  closeSidePanel,
  columnOrder,
  columnVisibility,
  folderId,
  isSidePanelOpen,
  onColumnOrderChange,
  onColumnVisibilityChange,
}) {
  const currentActor = useCurrentActor();

  const { seedDashboardConfigurableFields } = useProjectsDashboardContext();

  const sensors = useSensors(useSensor(PointerSensor));

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleDragEnd = ({ active, over }) => {
    onColumnOrderChange({ active, over });
  };

  const { singularRAMSName } = currentActor.division.attributes;

  const columnNames = useMemo(
    () =>
      buildColumnNames({
        dashboardConfigurableFields: seedDashboardConfigurableFields.data,
        projectReferenceLabel:
          currentActor.account.attributes.projectReferenceLabel,
        singularRAMSName: voca.capitalize(singularRAMSName),
        translations: {
          approvalStatus: i18nTCommon('projects.approval'),
          approver: i18nTCommon('projects.approver'),
          creationDate: i18nTCommon('shared.created'),
          creator: i18nTCommon('projects.creator'),
          folder: i18nTCommon('projects.folder'),
          lastUpdated: i18nTCommon('shared.lastUpdated'),
          live: i18nTCommon('shared.live'),
          reference: i18nTCommon('shared.reference'),
          reviewDate: i18nTCommon('projects.reviewDate'),
          reviewer: i18nTCommon('projects.reviewer'),
          riskAssessments: i18nTCommon('projects.riskAssessments'),
          signatureStatus: i18nTCommon('projects.signature'),
          startDate: i18nTCommon('shared.startDate'),
        },
      }),
    [
      currentActor.account.attributes.projectReferenceLabel,
      singularRAMSName,
      seedDashboardConfigurableFields.data,
    ],
  );

  const columnIds = columnOrder.filter(
    (columnId) =>
      ![...(folderId ? ['folder'] : []), 'selector', 'actions'].includes(
        columnId,
      ),
  );

  return (
    <SidePanel
      bodyContent={
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement, restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={columnIds}
            strategy={verticalListSortingStrategy}
          >
            <div className='-tw-mb-px -tw-mt-px tw-select-none tw-border-0 tw-border-b-1 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-bg-blue-025'>
              {columnIds.map((columnId) => (
                <SortableColumn
                  columnId={columnId}
                  columnName={columnNames[columnId]}
                  isColumnLocked={['live', 'project'].includes(columnId)}
                  isColumnVisible={columnVisibility[columnId]}
                  key={columnId}
                  onColumnVisibilityChange={onColumnVisibilityChange}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      }
      closeCallback={closeSidePanel}
      color='tertiary'
      displayClose={false}
      headerContent={
        <div className='tw-relative tw-flex tw-justify-between'>
          <div className='tw-w-full'>
            <h1 className='tw-m-0 tw-mb-3 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {i18nTCommon('shared.editColumns')}
            </h1>
            <div className='tw-m-0 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
              <div className='tw-mr-3 tw-h-5'>
                <InfoIcon
                  className='[&_path]:tw-fill-cyan-800'
                  height={20}
                  width={20}
                />
              </div>
              <p className='tw-m-0'>
                {i18nT('components.EditColumnsSidePanel.description')}
              </p>
            </div>
          </div>
          <div
            className="tw-absolute tw-right-0 tw-top-[5px] tw-h-[18px] tw-w-[18px] tw-cursor-pointer before:tw-absolute before:tw-left-2 before:tw-h-[18px] before:tw-w-[2px] before:tw-rotate-45 before:tw-bg-grey-500 before:tw-content-[''] after:tw-absolute after:tw-left-2 after:tw-h-[18px] after:tw-w-[2px] after:-tw-rotate-45 after:tw-bg-grey-500 after:tw-content-['']"
            data-element-name='side-panel-close'
            onClick={closeSidePanel}
          />
        </div>
      }
      isOpen={isSidePanelOpen}
    />
  );
}

EditColumnsSidePanel.propTypes = {
  closeSidePanel: PropTypes.func.isRequired,
  columnOrder: PropTypes.array.isRequired,
  columnVisibility: PropTypes.object.isRequired,
  folderId: PropTypes.string,
  isSidePanelOpen: PropTypes.bool.isRequired,
  onColumnOrderChange: PropTypes.func.isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
};

function SortableColumn({
  columnId,
  columnName,
  isColumnLocked,
  isColumnVisible,
  onColumnVisibilityChange,
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: columnId, disabled: isColumnLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={classNames(
        isColumnLocked && 'tw-pointer-events-none',
        isDragging && 'tw-relative tw-z-10',
      )}
      ref={isColumnLocked ? undefined : setNodeRef}
      style={style}
    >
      <div
        className={classNames(
          'tw-group -tw-mb-px -tw-mt-px tw-border-0 tw-border-b-1 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-bg-white [&:has([data-group-hover-disabled]:hover)]:tw-bg-white',
          !isDragging && 'hover:tw-bg-blue-025',
        )}
      >
        <div className='tw-ml-3 tw-mr-3 tw-flex tw-h-11 tw-items-center'>
          <div className='tw-flex tw-w-8 tw-items-center'>
            {isColumnLocked ?
              <LockIcon height={20} width={20} />
            : <div
                className={classNames(
                  'tw-flex tw-h-5 tw-w-5 tw-touch-none tw-items-center tw-justify-center tw-rounded-sm hover:tw-bg-blue-025 group-hover:tw-visible hover-hover:tw-invisible',
                  isDragging ?
                    'tw-cursor-grabbing tw-bg-blue-025'
                  : 'tw-cursor-grab',
                )}
                data-group-hover-disabled={true}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon
                  className='[&_path]:tw-fill-grey-700'
                  height={12}
                  width={8}
                />
              </div>
            }
          </div>
          <div
            className={classNames(
              'tw-w-full',
              isDragging && 'tw-pointer-events-none',
            )}
          >
            <CheckboxField
              checked={isColumnVisible}
              className='tw-block'
              disabled={isColumnLocked}
              label={columnName}
              labelTextProps={{ className: 'tw-block tw-w-full' }}
              name={columnId}
              onChange={
                isColumnLocked ?
                  () => {}
                : (event) => {
                    onColumnVisibilityChange({
                      columnId,
                      value: event.target.checked,
                    });
                  }
              }
              toggleProps={{ 'data-group-hover-disabled': true }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
